// Connections 模式:本地状态机(Unlimited + 前端 Daily)。谜题池/类别懒加载(进模式才下载)。
import { useEffect, useRef, useState } from 'react';
import { fnv1a, todayKey, gameNumber } from './game';
import { celebrate } from './confetti';
import { getConnGame, postConnGuess } from './api';

export interface ConnGroup {
  catId: string;
  tier: number;
  members: string[]; // cca3 ×4
}
export interface ConnPuzzle {
  id: number;
  groups: ConnGroup[];
}
export type ConnCategories = Record<string, { tier: number; names: Record<string, string> }>;

let cache: { pool: ConnPuzzle[]; cats: ConnCategories } | null = null;
export async function loadConnections(): Promise<{ pool: ConnPuzzle[]; cats: ConnCategories }> {
  if (cache) return cache;
  const [pool, cats] = await Promise.all([
    import('../data/connections-pool.json'),
    import('../data/connections-categories.json'),
  ]);
  cache = { pool: pool.default as ConnPuzzle[], cats: cats.default as ConnCategories };
  return cache;
}

export function dailyIndex(poolLen: number, day = todayKey()): number {
  return fnv1a(`${day}:connections`) % poolLen;
}

export type ConnStatus = 'loading' | 'playing' | 'won' | 'lost';

export interface ConnState {
  status: ConnStatus;
  cats: ConnCategories | null;
  puzzle: ConnPuzzle | null;
  tiles: string[]; // 未锁定的格子(cca3)
  selected: string[]; // ≤4
  locked: ConnGroup[]; // 已解出(或失败揭晓)的组,按解出顺序
  mistakes: number; // 0..4
  oneAway: boolean;
  shake: boolean;
  submissions: string[][]; // 每次提交的 picks(cca3);分享在结束后按已知分组上色
  num: number; // 期号
  endless: boolean;
  toggle: (c: string) => void;
  submit: () => void;
  shuffle: () => void;
  deselect: () => void;
  next: () => void; // unlimited 下一题
}

const MAX_MISTAKES = 4;

// Unlimited 会话连胜账本(与 Classic 共用 geodle:endless:<mode> 结构 → 喂进统一的 endless_best/Unlimited 榜)
export interface ConnLedger {
  solved: number;
  streak: number;
  best: number;
}
const CONN_ENDLESS_KEY = 'geodle:endless:connections';
export function connEndlessStats(): ConnLedger {
  try {
    const v = localStorage.getItem(CONN_ENDLESS_KEY);
    const s = v ? JSON.parse(v) : null;
    return { solved: s?.solved ?? 0, streak: s?.streak ?? 0, best: s?.best ?? 0 };
  } catch {
    return { solved: 0, streak: 0, best: 0 };
  }
}
function writeConnLedger(s: ConnLedger): void {
  try {
    localStorage.setItem(CONN_ENDLESS_KEY, JSON.stringify(s));
    window.dispatchEvent(new Event('geodle:endless'));
  } catch {
    /* ignore */
  }
}

function shuffleArr<T>(a: T[]): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
}

const sKey = (endless: boolean, day: string) => (endless ? 'geodle:conn:endless' : `geodle:conn:${day}`);
interface Save {
  puzzleId: number;
  lockedCatIds: string[];
  mistakes: number;
  status: ConnStatus;
  submissions: string[][];
}
function loadSave(endless: boolean, day: string): Save | null {
  try {
    const v = localStorage.getItem(sKey(endless, day));
    return v ? (JSON.parse(v) as Save) : null;
  } catch {
    return null;
  }
}
function persist(endless: boolean, day: string, s: Save) {
  try {
    localStorage.setItem(sKey(endless, day), JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export function useConnections(endless: boolean): ConnState {
  const day = todayKey();
  const num = gameNumber(day);
  const [cats, setCats] = useState<ConnCategories | null>(null);
  const [puzzle, setPuzzle] = useState<ConnPuzzle | null>(null);
  const [tiles, setTiles] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [locked, setLocked] = useState<ConnGroup[]>([]);
  const [mistakes, setMistakes] = useState(0);
  const [status, setStatus] = useState<ConnStatus>('loading');
  const [oneAway, setOneAway] = useState(false);
  const [shake, setShake] = useState(false);
  const [submissions, setSubmissions] = useState<string[][]>([]);
  const oneAwayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function begin(p: ConnPuzzle, restore: Save | null) {
    setPuzzle(p);
    const lockedGroups = restore
      ? (restore.lockedCatIds.map((id) => p.groups.find((g) => g.catId === id)).filter(Boolean) as ConnGroup[])
      : [];
    const lockedMembers = new Set(lockedGroups.flatMap((g) => g.members));
    setLocked(lockedGroups);
    setTiles(shuffleArr(p.groups.flatMap((g) => g.members).filter((m) => !lockedMembers.has(m))));
    setSelected([]);
    setMistakes(restore?.mistakes ?? 0);
    setSubmissions(restore?.submissions ?? []);
    setStatus(restore?.status && restore.status !== 'loading' ? restore.status : 'playing');
  }

  useEffect(() => {
    let on = true;
    loadConnections().then(({ pool, cats }) => {
      if (!on) return;
      setCats(cats);
      const saved = loadSave(endless, day);
      let p: ConnPuzzle;
      if (!endless) {
        p = pool[dailyIndex(pool.length, day)];
        begin(p, saved && saved.puzzleId === p.id ? saved : null);
      } else {
        p = saved ? pool[saved.puzzleId] ?? pool[Math.floor(Math.random() * pool.length)] : pool[Math.floor(Math.random() * pool.length)];
        begin(p, saved && saved.puzzleId === p.id ? saved : null);
      }
    });
    return () => {
      on = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endless, day]);

  function save(over: Partial<Save>) {
    if (!puzzle) return;
    persist(endless, day, {
      puzzleId: puzzle.id,
      lockedCatIds: locked.map((g) => g.catId),
      mistakes,
      status,
      submissions,
      ...over,
    });
  }

  function toggle(c: string) {
    if (status !== 'playing') return;
    setSelected((s) => (s.includes(c) ? s.filter((x) => x !== c) : s.length < 4 ? [...s, c] : s));
  }
  function deselect() {
    setSelected([]);
  }
  function shuffle() {
    setTiles((t) => shuffleArr(t));
  }

  function submit() {
    if (status !== 'playing' || selected.length !== 4 || !puzzle) return;
    const sel = new Set(selected);
    const newSubs = [...submissions, [...selected]];
    setSubmissions(newSubs);

    const lockedIds = new Set(locked.map((g) => g.catId));
    const matched = puzzle.groups.find(
      (g) => !lockedIds.has(g.catId) && g.members.every((m) => sel.has(m)),
    );

    if (matched) {
      const nextLocked = [...locked, matched];
      setLocked(nextLocked);
      setTiles((t) => t.filter((c) => !sel.has(c)));
      setSelected([]);
      if (nextLocked.length === 4) {
        setStatus('won');
        celebrate();
        if (endless) {
          const l = connEndlessStats();
          const streak = l.streak + 1;
          writeConnLedger({ solved: l.solved + 1, streak, best: Math.max(l.best, streak) });
        } else {
          window.dispatchEvent(new Event('geodle:progress')); // 每日通关 → 触发同步 + 榜单刷新
        }
        save({ lockedCatIds: nextLocked.map((g) => g.catId), status: 'won', submissions: newSubs });
      } else {
        save({ lockedCatIds: nextLocked.map((g) => g.catId), submissions: newSubs });
      }
    } else {
      // one away?
      const oa = puzzle.groups.some(
        (g) => !lockedIds.has(g.catId) && g.members.filter((m) => sel.has(m)).length === 3,
      );
      setOneAway(oa);
      if (oneAwayTimer.current) clearTimeout(oneAwayTimer.current);
      if (oa) oneAwayTimer.current = setTimeout(() => setOneAway(false), 2400);
      setShake(true);
      setTimeout(() => setShake(false), 480);
      const m = mistakes + 1;
      setMistakes(m);
      if (m >= MAX_MISTAKES) {
        const remaining = puzzle.groups.filter((g) => !lockedIds.has(g.catId)).sort((a, b) => a.tier - b.tier);
        const all = [...locked, ...remaining];
        setLocked(all);
        setTiles([]);
        setSelected([]);
        setStatus('lost');
        if (endless) writeConnLedger({ ...connEndlessStats(), streak: 0 });
        save({ lockedCatIds: all.map((g) => g.catId), mistakes: m, status: 'lost', submissions: newSubs });
      } else {
        save({ mistakes: m, submissions: newSubs });
      }
    }
  }

  function next() {
    if (!endless || !cache) return;
    const p = cache.pool[Math.floor(Math.random() * cache.pool.length)];
    persist(endless, day, { puzzleId: p.id, lockedCatIds: [], mistakes: 0, status: 'playing', submissions: [] });
    begin(p, null);
  }

  return {
    status,
    cats,
    puzzle,
    tiles,
    selected,
    locked,
    mistakes,
    oneAway,
    shake,
    submissions,
    num,
    endless,
    toggle,
    submit,
    shuffle,
    deselect,
    next,
  };
}

// ===== 服务器权威 Daily(竞技;客户端拿不到分组) =====
export function useConnectionsServer(onOffline: () => void): ConnState {
  const day = todayKey();
  const num = gameNumber(day);
  const [cats, setCats] = useState<ConnCategories | null>(null);
  const [tiles, setTiles] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [locked, setLocked] = useState<ConnGroup[]>([]);
  const [mistakes, setMistakes] = useState(0);
  const [status, setStatus] = useState<ConnStatus>('loading');
  const [oneAway, setOneAway] = useState(false);
  const [shake, setShake] = useState(false);
  const [submissions, setSubmissions] = useState<string[][]>([]);
  const busy = useRef(false);
  const oneAwayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let on = true;
    Promise.all([loadConnections(), getConnGame(day)])
      .then(([c, g]) => {
        if (!on) return;
        setCats(c.cats);
        setTiles(g.tiles);
        setLocked(g.locked);
        setMistakes(g.mistakes);
        setSubmissions(g.submissions);
        setStatus(g.status);
      })
      .catch(() => on && onOffline());
    return () => {
      on = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  function toggle(c: string) {
    if (status !== 'playing' || busy.current) return;
    setSelected((s) => (s.includes(c) ? s.filter((x) => x !== c) : s.length < 4 ? [...s, c] : s));
  }
  function deselect() {
    setSelected([]);
  }
  function shuffle() {
    setTiles((t) => shuffleArr(t));
  }

  async function submit() {
    if (status !== 'playing' || selected.length !== 4 || busy.current) return;
    busy.current = true;
    const picks = [...selected];
    setSubmissions((s) => [...s, picks]);
    try {
      const resp = await postConnGuess(day, picks);
      setMistakes(resp.mistakes);
      if (resp.result === 'correct' && resp.group) {
        setLocked(resp.locked);
        setTiles((t) => t.filter((c) => !picks.includes(c)));
        setSelected([]);
        if (resp.status === 'won') {
          setStatus('won');
          celebrate();
          window.dispatchEvent(new Event('geodle:progress')); // 竞技通关 → 触发同步 + 榜单刷新
        }
      } else {
        setOneAway(resp.result === 'oneAway');
        if (oneAwayTimer.current) clearTimeout(oneAwayTimer.current);
        if (resp.result === 'oneAway') oneAwayTimer.current = setTimeout(() => setOneAway(false), 2400);
        setShake(true);
        setTimeout(() => setShake(false), 480);
        if (resp.status === 'lost') {
          setLocked(resp.locked);
          setTiles([]);
          setSelected([]);
          setStatus('lost');
        }
      }
    } catch {
      setSubmissions((s) => s.slice(0, -1)); // 回滚本次提交
      setShake(true);
      setTimeout(() => setShake(false), 480);
    } finally {
      busy.current = false;
    }
  }

  return {
    status,
    cats,
    puzzle: null,
    tiles,
    selected,
    locked,
    mistakes,
    oneAway,
    shake,
    submissions,
    num,
    endless: false,
    toggle,
    submit,
    shuffle,
    deselect,
    next: () => {},
  };
}
